/**
 * QuickBooks Online error vocabulary.
 *
 * One place that answers "what is wrong with this tenant's QBO connection?", so
 * the invoice screen and the integrations screen cannot drift apart on it.
 *
 * Verified against fieldiq-api `main` @ fc6325e (quickbooks.routes.ts,
 * quickbooks.service.ts), not assumed.
 */

import type { QboReadinessBlocker, QboPushResult } from '../types/qbo-mapping';

/** The API's error code for a grant Intuit has revoked. */
export const QBO_RECONNECT_CODE = 'QBO_RECONNECT_REQUIRED';

/** Where a tenant goes to fix either fault. */
export const QBO_RECONNECT_ROUTE = '/settings/integrations';

/**
 * The two ways a QBO push can fail for a reason the operator can act on.
 *
 * They are NOT the same fault and must not share copy: `revoked` means a
 * connection existed and Intuit killed it, so the action is Reconnect;
 * `not_connected` means this tenant never linked QuickBooks at all, so the
 * action is Connect. Telling a tenant who has never connected that their
 * connection "expired" sends them looking for a lapse that never happened.
 */
export type QboConnectionFault = 'revoked' | 'not_connected';

/** Single phrasing per fault. Do not re-word these at a call site. */
export const QBO_FAULT_TITLE: Record<QboConnectionFault, string> = {
  revoked: 'QuickBooks connection expired',
  not_connected: 'QuickBooks is not connected',
};

export const QBO_FAULT_MESSAGE: Record<QboConnectionFault, string> = {
  revoked: 'QuickBooks connection expired — reconnect in Settings → Integrations',
  not_connected: 'QuickBooks has not been connected — connect it in Settings → Integrations',
};

/**
 * Link label. Deliberately NOT another "Settings → Integrations" — the message
 * above it already names the destination, and a third repetition in the one
 * banner reads as boilerplate. This is the affordance, so it names the action.
 */
export const QBO_FAULT_CTA: Record<QboConnectionFault, string> = {
  revoked: 'Reconnect QuickBooks',
  not_connected: 'Connect QuickBooks',
};

/**
 * The one message `pushInvoice` still throws as a plain 500 for a tenant that
 * has never linked QuickBooks (quickbooks.service.ts:245). Distinct from the
 * revoked grant, which the API raises as `QboAuthRevokedError` and the route
 * answers as 409 + a code — see `QboConnectionFault` above.
 */
const NOT_CONNECTED_MESSAGE = 'QuickBooks not connected';

/** Read an error code off an axios error body, tolerating the data envelope. */
function readCode(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const top = (body as Record<string, unknown>).code;
  if (typeof top === 'string') return top;
  const inner = (body as Record<string, unknown>).data;
  if (inner && typeof inner === 'object') {
    const nested = (inner as Record<string, unknown>).code;
    if (typeof nested === 'string') return nested;
  }
  return null;
}

/** Read the human message off an axios error body. */
export function readQboErrorMessage(err: unknown): string | null {
  const body = (err as { response?: { data?: unknown } } | null)?.response?.data;
  if (!body || typeof body !== 'object') return null;
  const message = (body as Record<string, unknown>).error;
  return typeof message === 'string' && message.trim() ? message.trim() : null;
}

/**
 * Which connection fault this failure is, or null when it is an ordinary error.
 *
 * A network error with no response at all is null — an unreachable API is not a
 * dead connection, and offering Reconnect for one would send the operator to a
 * page that cannot load either.
 */
export function qboConnectionFault(err: unknown): QboConnectionFault | null {
  const response = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
  if (!response) return null;

  if (response.status === 409 && readCode(response.data) === QBO_RECONNECT_CODE) return 'revoked';

  if (response.status === 500 && readQboErrorMessage(err) === NOT_CONNECTED_MESSAGE) {
    return 'not_connected';
  }

  return null;
}

/**
 * Friendly text for the `?qbo=error&reason=` OAuth return.
 *
 * The API's `reasonCode()` is a CLOSED vocabulary of exactly these four — it
 * maps anything it does not recognise to `unknown` itself, deliberately, so the
 * provider's own message can never reach a browser address bar. Keep this map
 * in step with that function rather than guessing at OAuth2 spec codes: an
 * earlier version of this file mapped `access_denied`, `invalid_scope` and
 * `server_error`, none of which the API can ever emit.
 */
const OAUTH_REASONS: Record<string, string> = {
  invalid_grant:
    'The authorisation was rejected by QuickBooks. Connect again to grant a fresh one.',
  state_invalid:
    'The connection attempt expired or had already been used. Start it again from this page.',
  token_exchange_failed:
    'QuickBooks refused the connection request. Wait a moment and try again.',
  unknown:
    'QuickBooks did not say why. Try again, and contact support if it keeps happening.',
};

const MAX_REASON_CHARS = 120;

export function qboOAuthErrorMessage(reason: string | null | undefined): string {
  const base = 'QuickBooks could not be connected.';
  const trimmed = typeof reason === 'string' ? reason.trim() : '';

  // The API always appends a reason now, so an absent one means a hand-typed URL
  // or a deployment older than fc6325e — worth handling, not worth detailing.
  if (!trimmed) return `${base} Try connecting again.`;

  const known = OAUTH_REASONS[trimmed];
  if (known) return `${base} ${known}`;

  // Only reachable if the API's closed vocabulary grows. Echo rather than
  // swallow: a code an operator can quote to support beats a generic sentence.
  const shown = trimmed.length > MAX_REASON_CHARS
    ? `${trimmed.slice(0, MAX_REASON_CHARS)}…`
    : trimmed;
  return `${base} QuickBooks reported: ${shown}`;
}

// ─── Push refusals (API #887) ──────────────────────────────────────────────

/** The mapping screen, and where each blocker kind's tab lives on it. */
export const QBO_MAPPING_ROUTE = '/settings/integrations/quickbooks-mapping';

export function qboMappingTabHref(kind: string): string {
  return `${QBO_MAPPING_ROUTE}?kind=${encodeURIComponent(kind)}`;
}

/** The one sentence beside a Push button the company is not ready for. */
export const QBO_PUSH_OFF_REASON = 'Push is off until QuickBooks mapping is complete';

/**
 * A push the API REFUSED with a reason the office can read, as opposed to a
 * fault. Every `message` is the API's own `error` sentence, verbatim — the
 * route writes them for people, so they are not re-worded here.
 *
 * Tone travels with the kind, because colour is part of the message:
 *   already_pushed, in_progress — INFORMATIONAL. Nothing went wrong; the
 *     invoice is (or is about to be) in QuickBooks. Never the error style and
 *     never the success style (fe#421/#452: a refusal rendered under a tick).
 *   docnumber_exists — a WARNING somebody has to resolve in QuickBooks.
 *   not_ready, mapping_required — a ROUTE to the mapping screen.
 */
export type QboPushRefusal =
  | { kind: 'already_pushed'; message: string; qboInvoiceId: string | null }
  | { kind: 'in_progress'; message: string; startedAt: string | null }
  | { kind: 'docnumber_exists'; message: string; qboInvoiceIds: string[] }
  | { kind: 'not_ready'; message: string; blockers: QboReadinessBlocker[] }
  | { kind: 'mapping_required'; message: string; mappingKind: string; missingKeys: string[] };

export type QboPushRefusalKind = QboPushRefusal['kind'];

function strings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

function blockersOf(v: unknown): QboReadinessBlocker[] {
  if (!Array.isArray(v)) return [];
  return v.filter((b): b is QboReadinessBlocker =>
    !!b && typeof b === 'object'
    && ['item', 'rate_sheet_item', 'tax_code'].includes((b as QboReadinessBlocker).kind)
    && typeof (b as QboReadinessBlocker).message === 'string');
}

/**
 * Which refusal this push failure is, or null for anything else (a fault, a
 * network error, a 5xx). Read off `code` ONLY — `QBO_NOT_READY` carries
 * `mappingKind` and `missingKeys` too, because the API's class extends the
 * mapping refusal, so inferring the kind from which fields are present would
 * call it `mapping_required`.
 */
export function readQboPushRefusal(err: unknown): QboPushRefusal | null {
  const response = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
  if (!response || response.status !== 409) return null;
  const body = response.data;
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const code = readCode(body);
  const api = readQboErrorMessage(err);

  switch (code) {
    case 'QBO_ALREADY_PUSHED':
      return {
        kind: 'already_pushed',
        message: api ?? 'This invoice is already in QuickBooks.',
        qboInvoiceId: typeof b.qboInvoiceId === 'string' ? b.qboInvoiceId : null,
      };
    case 'QBO_PUSH_IN_PROGRESS':
      return {
        kind: 'in_progress',
        message: api ?? 'This invoice is being pushed to QuickBooks right now.',
        startedAt: typeof b.startedAt === 'string' ? b.startedAt : null,
      };
    case 'QBO_DOCNUMBER_EXISTS':
      return {
        kind: 'docnumber_exists',
        message: api ?? 'QuickBooks already holds an invoice with this number.',
        qboInvoiceIds: strings(b.qboInvoiceIds),
      };
    case 'QBO_NOT_READY':
      return {
        kind: 'not_ready',
        message: api ?? QBO_PUSH_OFF_REASON,
        blockers: blockersOf(b.blockers),
      };
    case 'QBO_MAPPING_REQUIRED':
      return {
        kind: 'mapping_required',
        message: api ?? 'QuickBooks mapping is required before this invoice can be pushed.',
        mappingKind: typeof b.mappingKind === 'string' ? b.mappingKind : 'customer',
        missingKeys: strings(b.missingKeys),
      };
    default:
      return null;
  }
}

/**
 * The success sentence. An ADOPTED push created nothing — QuickBooks already
 * held this exact invoice and FieldIQ linked to it — so it must not read
 * "Pushed".
 */
export function qboPushSuccessMessage(result: Partial<QboPushResult> | null | undefined): string {
  const id = result?.qboInvoiceId ? ` #${result.qboInvoiceId}` : '';
  return result?.adopted === true
    ? `Linked to the existing QuickBooks invoice${id}`
    : `Pushed to QuickBooks as${id || ' a new invoice'}`;
}
